import { initializePaddle, Paddle } from '@paddle/paddle-js'
import { AccountPublic } from '@pollination-solutions/pollination-sdk'
import { useCallback, useEffect, useState } from 'react'


interface BillingCheckoutProps {
  url: string
  items: { priceId: string; quantity?: number }[]
  account: AccountPublic
  eventCallback?: (event: { eventType: string; data: unknown }) => void
  onSuccess: (data: unknown, err: unknown) => void
}

const PADDLE_ENV = process.env.REACT_APP_PROJECT_ID && !process.env.REACT_APP_PROJECT_ID.includes('staging') ? 'production' : 'sandbox'
const PADDLE_TOKEN = PADDLE_ENV === 'production' ? 'live_c03aa19481d3559bc24312995e8' : 'test_ea1e6a83a2177418fad3cc35d04'
const PADDLE_DEBUG = PADDLE_ENV === 'production' ? false : true

export const usePaddle = () => {
  const [paddle, setPaddle] = useState<Paddle | null>(null)
  const [isInitializing, setIsInitializing] = useState(true)
  const [initError, setInitError] = useState<string | null>(null)

  // Download and initialize Paddle instance from CDN
  useEffect(() => {
    initializePaddle({
      environment: PADDLE_ENV,
      token: PADDLE_TOKEN,
      debug: PADDLE_DEBUG,
    }).then(
      (paddleInstance: Paddle | undefined) => {
        setIsInitializing(false)
        if (paddleInstance) {
          setPaddle(paddleInstance)
        } else {
          setInitError('Failed to initialize Paddle')
        }
      },
    ).catch((error) => {
      setIsInitializing(false)
      setInitError(error.message || 'Failed to initialize Paddle')
    })
  }, [])


  const openCheckout = useCallback((props: BillingCheckoutProps) => {
    if (!paddle) {
      props.onSuccess(null, 'Paddle not initialized')
      return
    }

    const transactionPayload = {
      settings: {
        successUrl: props.url,
        showAddDiscounts: true,
      },
      items: props.items,
      customData: {
        account_id: props.account.id
      },
      eventCallback: props.eventCallback,
    }

    try {
      paddle.Checkout.open(transactionPayload)
    } catch (error) {
      console.error('Failed to open Paddle checkout:', error)
      props.onSuccess(null, error)
    }
  }, [paddle])

  const openPaymentMethodUpdate = useCallback((props: { transactionId: string }) => {
    if (!paddle) {
      console.error('Paddle not initialized')
      return
    }

    const transactionPayload = {
      settings: {
        successUrl: window.location.href,
      },
      transactionId: props.transactionId,
    }

    try {
      paddle.Checkout.open(transactionPayload)
    } catch (error) {
      console.error('Failed to open Paddle payment method update:', error)
    }
  }, [paddle])

  // Expose the Paddle instance and methods
  return {
    openCheckout,
    openPaymentMethodUpdate,
    isInitializing,
    initError,
    isReady: !!paddle && !isInitializing && !initError
  }
}

export default usePaddle
